import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { useIntl } from 'react-intl'; // TODO: Replace react-intl with intl-next for hook usage
import type { Dispatch } from 'model/State';

import Header from 'components/navigation/top/Header';
import BottomMenu from 'components/navigation/bottom/BottomMenu';
import PageWidthContainer from 'components/navigation/PageWidthContainer';
import SecondaryMenu from 'components/navigation/secondary/SecondaryMenu';
import PageSwitcher from 'components/PageSwitcher';
import { State } from 'model/State';
import WarningMessage from 'components/navigation/top/WarningMessage';
import { FormStateProvider } from 'modules/stripe';
import { Dimensions } from 'components/visuals';
import { redirect } from 'redux-first-router';
import { goToProductList } from 'actions';
import routesMap from 'routes';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';
import config from 'appconfig';
import traceUFOPageLoad from '@atlassian/react-ufo/trace-pageload';
import traceUFOTransition from '@atlassian/react-ufo/trace-transition';
import * as contacts from 'modules/checkout/contacts';

type Props = {
  location: string;
  showHeader: boolean;
  displayMACTopMenu: boolean;
  isStripeCCFormIncludedOnPage: boolean;
  redirectToProductList: () => void;
  ufoName: string;
};

const shouldDisplayMACTopMenu = (state) => {
  const route = routesMap[state.location.type];
  const pageOptions = route ? route.pageOptions : null;
  return pageOptions ? pageOptions.isMACPage : false;
};

const isStripeCCFormOnPage = (state) => {
  const route = routesMap[state.location.type];
  const pageOptions = route ? route.pageOptions : null;
  return pageOptions ? pageOptions.includesStripeCCForm : false;
};

const DivForSpace = styled.div`
  height: ${Dimensions.units(8)}px;
`;

const AppSwitcher: React.FC<Props> = ({
  ufoName,
  location,
  showHeader,
  displayMACTopMenu,
  isStripeCCFormIncludedOnPage,
  redirectToProductList,
}) => {
  const isInitial = useRef(true);
  const intl = useIntl();

  useEffect(() => {
    document.title = intl.formatMessage({ id: 'document.title.general' });
  }, []);

  useEffect(() => {
    isInitial.current = false;
  }, []);

  useEffect(() => {
    if (ufoName) {
      if (isInitial.current) {
        traceUFOPageLoad(ufoName, ufoName);
      } else {
        traceUFOTransition(ufoName);
      }
    }
    // Fallback for checkout contacts route if ufoName is missing
    if (!ufoName && location === contacts.actions.ROUTE) {
      if (isInitial.current) {
        traceUFOPageLoad('checkout-contacts-route', 'checkout-contacts-route');
      } else {
        traceUFOTransition('checkout-contacts-route');
      }
    }
  }, [ufoName, location]);

  const isSiteConfiguratorCleanupFGOn = checkFeatureGate(
    FeatureFlags.CART_SITE_CONFIGURATOR_CLEANUP,
  );

  if (window.location.href.includes('/purchase/product/jira-core')) redirectToProductList();
  if (window.location.href.includes('/purchase/product/fisheye')) redirectToProductList();
  if (window.location.href.includes('/purchase/product/crucible')) redirectToProductList();
  if (isSiteConfiguratorCleanupFGOn && window.location.href.includes('/purchase/siteconfigurator'))
    redirectToProductList();

  if (location === 'MIGRATE' || location === 'BUILDER') {
    window.location.href = config.wacLicensingUrl;
    return null;
  }

  const isRenewUpgrade =
    location.includes('renew') ||
    (location.includes('upgrade') && !location.includes('upgrade/datacenter'));

  const isRenewUpgradeView = isRenewUpgrade ? <DivForSpace /> : <WarningMessage />;

  return (
    <>
      <PageWidthContainer dataTestId="page-content">
        {showHeader && (
          <>
            <Header isMACPage={displayMACTopMenu} />
            {!displayMACTopMenu && isRenewUpgradeView}
            <SecondaryMenu />
          </>
        )}
        {/* TODO: Review and fix missing attributes */}
        <FormStateProvider isStripeCCFormIncludedOnPage={isStripeCCFormIncludedOnPage}>
          <PageSwitcher />
        </FormStateProvider>
      </PageWidthContainer>
      {showHeader && <BottomMenu />}
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  redirectToProductList: () => dispatch(redirect(goToProductList())),
});

export default connect(
  (state: State) => ({
    // TODO: Review RoutesMap type, maybe routes/index.js should be rewritten to typescript
    ufoName: (state.location.routesMap[state.location.type] as any).ufoName,
    location: state.location.type,
    showHeader: state.navigation.displayHeader,
    displayMACTopMenu: shouldDisplayMACTopMenu(state),
    isStripeCCFormIncludedOnPage: isStripeCCFormOnPage(state),
  }),
  mapDispatchToProps,
)(AppSwitcher);
